import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, FolderTree, Loader2, Image } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ImageUploader } from '../../components/ImageUploader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get full image URL
const getImageUrl = (img) => {
  if (!img) return '';
  if (img.startsWith('http://') || img.startsWith('https://')) {
    return img;
  }
  if (img.startsWith('/api/uploads/') || img.startsWith('/uploads/')) {
    const path = img.startsWith('/uploads/') ? img.replace('/uploads/', '/api/uploads/') : img;
    return `${API_URL}${path}`;
  }
  return img;
};

const emptyCategory = {
  name: '',
  slug: '',
  description: '',
  image: ''
};

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [formData, setFormData] = useState(emptyCategory);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditCategory(category);
      setFormData({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        image: category.image || ''
      });
    } else {
      setEditCategory(null);
      setFormData(emptyCategory);
    }
    setDialogOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      // Auto-generate slug from name
      if (name === 'name' && !editCategory) {
        newData.slug = value.toLowerCase()
          .replace(/[éèêë]/g, 'e')
          .replace(/[àâä]/g, 'a')
          .replace(/[ùûü]/g, 'u')
          .replace(/[îï]/g, 'i')
          .replace(/[ôö]/g, 'o')
          .replace(/[ç]/g, 'c')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');

      if (editCategory) {
        await axios.put(`${API_URL}/api/categories/${editCategory.category_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Catégorie mise à jour');
      } else {
        await axios.post(`${API_URL}/api/categories`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Catégorie créée');
      }
      
      setDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (!window.confirm(`Supprimer la catégorie "${categoryName}" ?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-categories">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl text-brand-black flex items-center gap-3">
              <FolderTree className="h-8 w-8 text-brand-gold" />
              Catégories
            </h1>
            <p className="text-brand-dark/60">{categories.length} catégories</p>
          </div>
          <Button 
            onClick={() => handleOpenDialog()} 
            className="bg-brand-gold text-white hover:bg-brand-gold"
            data-testid="add-category-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Nouvelle catégorie
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-12">
            <FolderTree className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-brand-dark/60">Aucune catégorie</p>
            <Button onClick={() => handleOpenDialog()} className="mt-4 bg-brand-gold text-white hover:bg-brand-gold">
              Créer la première catégorie
            </Button>
          </div>
        ) : (
          <div className="bg-brand-white border border-brand-gold/20 rounded shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-brand-gold/20 bg-brand-cream">
                  <TableHead className="text-brand-dark/80">Image</TableHead>
                  <TableHead className="text-brand-dark/80">Nom</TableHead>
                  <TableHead className="text-brand-dark/80">Slug</TableHead>
                  <TableHead className="text-brand-dark/80">Description</TableHead>
                  <TableHead className="text-brand-dark/80 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map(category => (
                  <TableRow key={category.category_id} className="border-brand-gold/10 hover:bg-brand-cream">
                    <TableCell>
                      {category.image ? (
                        <img 
                          src={getImageUrl(category.image)} 
                          alt={category.name}
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/48?text=No+img'; }}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-brand-cream rounded flex items-center justify-center">
                          <Image className="h-5 w-5 text-gray-300" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-brand-black font-medium">{category.name}</TableCell>
                    <TableCell className="text-brand-dark/60 font-mono text-sm">{category.slug}</TableCell>
                    <TableCell className="text-brand-dark/80 max-w-xs truncate">{category.description || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleOpenDialog(category)} 
                        className="text-brand-dark/60 hover:text-brand-gold"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(category.category_id, category.name)} 
                        className="text-brand-dark/60 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Category Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-brand-white border-brand-gold/20 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-brand-black">
                {editCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-brand-dark">Nom de la catégorie *</Label>
                <Input 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                  placeholder="Ex: Cosmétiques"
                  className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black" 
                />
              </div>

              <div>
                <Label className="text-brand-dark">Slug (URL) *</Label>
                <Input 
                  name="slug" 
                  value={formData.slug} 
                  onChange={handleChange} 
                  required 
                  placeholder="Ex: cosmetiques"
                  className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black font-mono" 
                />
                <p className="text-brand-dark/50 text-xs mt-1">Utilisé dans l'URL: /products?category={formData.slug || 'slug'}</p>
              </div>

              <div>
                <Label className="text-brand-dark">Description</Label>
                <Textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleChange} 
                  rows={3}
                  placeholder="Description de la catégorie..."
                  className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black resize-none" 
                />
              </div>

              <div>
                <Label className="text-brand-dark">Image de la catégorie</Label>
                <div className="mt-2">
                  <ImageUploader 
                    images={formData.image ? [formData.image] : []} 
                    onChange={(newImages) => setFormData(prev => ({ ...prev, image: newImages[0] || '' }))}
                    maxImages={1}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-brand-dark/60">
                  Annuler
                </Button>
                <Button type="submit" disabled={saving} className="bg-brand-gold text-white hover:bg-brand-gold">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editCategory ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
